import { useMemo } from 'react';
import {
  SystemClassifierCode,
  SystemClassifierDto,
  SystemClassifierItemDto,
} from '../systemClassifier';
import { useSystemClassifiers } from './useSystemClassifiers';

export interface SystemClassifierOption {
  value: string | number;
  label: string;
  code: string;
}

type OptionMapper = (
  systemClassifierItem: SystemClassifierItemDto
) => Record<string, SystemClassifierItemDto[keyof SystemClassifierItemDto]>;

interface UseSystemClassifierSelectOptions {
  <Code extends SystemClassifierCode>(codes: Code[], valueProp?: never): {
    [K in Code]: SystemClassifierOption[];
  };
  <
    Code extends SystemClassifierCode,
    ValueProp extends keyof SystemClassifierItemDto | OptionMapper,
    Options extends ValueProp extends (...args: any) => infer R
      ? { [K in Code]: R[] }
      : { [K in Code]: SystemClassifierOption[] }
  >(
    codes: Code[],
    valueProp: ValueProp
  ): Options;
}

export const useSystemClassifierSelectOptions: UseSystemClassifierSelectOptions =
  <Codes extends SystemClassifierCode[]>(
    codes: Codes,
    valueProp: keyof SystemClassifierItemDto | OptionMapper = 'id'
  ) => {
    const systemClassifiers = useSystemClassifiers(...codes);

    return useMemo(
      () =>
        (
          Object.entries(systemClassifiers) as [
            Codes[number],
            SystemClassifierDto
          ][]
        ).reduce(
          (acc, [code, systemClassifier]) => ({
            ...acc,
            [code]: getOptionsFromSystemClassifierItems(
              systemClassifier.systemClassifierItems,
              valueProp
            ),
          }),
          {} as {
            [K in Codes[number]]: SystemClassifierOption[];
          }
        ),
      [systemClassifiers, valueProp]
    );
  };

export const getOptionsFromSystemClassifierItems = (
  systemClassifierItems: SystemClassifierItemDto[],
  valueProp?: keyof SystemClassifierItemDto | OptionMapper
) => {
  const items = systemClassifierItems.toSorted(
    (a, b) =>
      (a.sequenceNumber ?? Number.MAX_SAFE_INTEGER) -
      (b.sequenceNumber ?? Number.MAX_SAFE_INTEGER)
  );

  if (valueProp instanceof Function) {
    return items.map(valueProp);
  }

  const key = (valueProp ?? 'id') as keyof SystemClassifierItemDto;

  return items.map((item) => ({
    value: ['string', 'number'].includes(typeof item[key])
      ? item[key]
      : item[key].toString(),
    label: item.name,
    code: item.code,
  }));
};
